import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Sector,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useSpeciesStats } from '../../../hooks/useSpeciesStats';
import {
  SPECIES_OTHER_INDEX,
  SPECIES_PALETTE,
  SPECIES_TOP_N,
} from '../../../lib/speciesColors';
import { SpeciesCompositionRow } from '../../../lib/types';
import {
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { StatCard } from './StatCard';
import { AboutLayer } from './AboutLayer';
import { GlossaryTerm } from '../../shared/GlossaryTerm';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
  SkeletonStatCardGrid,
} from './contentPrimitives';

interface SpeciesContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
}

interface PieSlice {
  name: string;
  value: number;        // percent (0-100)
  color: string;
  isOther: boolean;
}

// Collapse any ranked rows beyond TOP_N into the Other slice so the pie's
// colors exactly match the dominant map overlay (which only has palette slots
// for top-10 + Other). The CSV may carry 11+ ranked rows when a shed has lots
// of species above the 0.5% threshold (Sierra Nevada has 16).
const buildPieSlices = (rows: SpeciesCompositionRow[]): PieSlice[] => {
  const topRows = rows.filter(r => !r.isOther && r.rank <= SPECIES_TOP_N);
  const otherRows = rows.filter(r => r.isOther || r.rank > SPECIES_TOP_N);
  const otherFraction = otherRows.reduce((s, r) => s + r.fractionOfBiomass, 0);

  const slices: PieSlice[] = topRows.map(r => ({
    name: r.speciesName,
    value: r.fractionOfBiomass * 100,
    color: SPECIES_PALETTE[r.rank] ?? '#cccccc',
    isOther: false,
  }));

  if (otherFraction > 0) {
    slices.push({
      name: 'Other',
      value: otherFraction * 100,
      color: SPECIES_PALETTE[SPECIES_OTHER_INDEX] ?? '#cccccc',
      isOther: true,
    });
  }
  return slices;
};

const Skeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.species_diversity} />
    <SkeletonPulseBlock className="h-[260px]" />
    <SkeletonStatCardGrid />
  </div>
);

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

// Custom active-slice renderer — expands the hovered/active sector outward by
// a few pixels and adds a light stroke so the highlight follows the slice's
// actual arc geometry instead of Recharts' default bounding-box highlight.
// Props are loosely typed because Recharts' PieSectorDataItem has most fields
// optional at the type level, even though they're always present at runtime.
const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      stroke="#FBFFEF"
      strokeWidth={1.5}
    />
  );
};

export const SpeciesContent: React.FC<SpeciesContentProps> = ({
  millCode,
  driveTimeMinutes,
}) => {
  const layer = DATA_LAYERS.species_diversity;
  const { data, isLoading, error } = useSpeciesStats(millCode, driveTimeMinutes);

  const slices = data ? buildPieSlices(data.rows) : [];

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          Species diversity captures the mix of tree species that make up the shed&rsquo;s
          live biomass. Stands dominated by a single species are more exposed to
          species-specific threats (insect outbreaks, pathogens, climate stress);
          stands with a broader species mix tend to be more resilient.
        </p>
        <p>
          Values come from the USDA Forest Service BIGMap Individual Tree
          Species-Level Biomass product (30&thinsp;m, 2018 epoch). Each pixel
          carries a per-species above-ground biomass estimate; the shed&rsquo;s
          composition is computed by summing biomass within the supply-shed
          polygon, across 327 species.
        </p>
        <p>
          Three summary indices are surfaced below the pie chart:{' '}
          <GlossaryTerm term="shannon_index">Shannon</GlossaryTerm> (combines
          richness and evenness in one number), Simpson (a similar index that
          weights common species more heavily), and{' '}
          <GlossaryTerm term="pielou_evenness">Pielou&rsquo;s evenness</GlossaryTerm>
          {' '}(a 0&ndash;1 score for how evenly biomass is split across species,
          independent of how many there are).
        </p>
      </AboutLayer>

      {isLoading && <Skeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && slices.length > 0 && (
        <>
          <LayerLiveBadge />

          <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
            <h4 className="text-base font-roboto font-bold text-figma-text mb-4">
              Species Composition by Biomass <span className="text-neutral-500 font-normal">(% of AGB)</span>
            </h4>
            <ResponsiveContainer width="100%" height={320} minWidth={0} className="species-pie">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={100}
                  paddingAngle={1}
                  isAnimationActive={false}
                  activeShape={renderActiveShape}
                >
                  {slices.map((s, i) => (
                    <Cell key={`${s.name}-${i}`} fill={s.color} stroke="#1f2937" strokeWidth={1} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                  formatter={(value: number | undefined, name: string | undefined) => [
                    fmtPct(value ?? 0),
                    name ?? '',
                  ]}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{ fontSize: 12, fontFamily: 'Roboto, sans-serif', color: '#FBFFEF' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
              Slice colors match the dominant-species map overlay. Species below
              the 0.5% biomass threshold and any species ranked beyond the top 10
              are rolled into the &ldquo;Other&rdquo; slice so the color legend
              stays consistent between the pie and the map.
            </p>
          </div>

          {/* Bottom row (Evenness, Richness) flips tooltip to 'top' so the
              tooltip extends above the card — otherwise it would clip below
              the DetailLeftPanel's overflow-y scroll container when the panel
              is scrolled to the end of content. */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label="Shannon (H')"
              value={data.shannon.toFixed(2)}
              info="Shannon diversity index, −Σ p·ln(p) over species biomass fractions. Higher values indicate more diversity; typical forests range from 0 (monoculture) to ~3."
            />
            <StatCard
              label="Simpson (1-D)"
              value={data.simpson.toFixed(2)}
              info="Gini-Simpson index, 1 − Σ p². The probability that two randomly drawn biomass units belong to different species. Bounded 0–1; higher = more diverse."
            />
            <StatCard
              label="Evenness (J)"
              value={data.evenness.toFixed(2)}
              info="Pielou's evenness, J = H' / ln(S). Rescales Shannon by the number of named species; equally-abundant distributions land at J = 1 regardless of richness."
              tooltipPlacement="top"
            />
            <StatCard
              label="Richness"
              value={`${data.richness} spp.`}
              info="Count of species whose shed-wide biomass is above the 0.5% threshold. Species below it are collapsed into the Other slice. Distinct from the map's per-pixel richness, which counts species co-occurring within a single 30 m pixel."
              tooltipPlacement="top"
            />
          </div>
        </>
      )}
    </div>
  );
};
